import { siteConfig } from "@/mocks/comics";

interface SEOSchemaProps {
  type: "WebSite" | "Organization" | "WebPage";
  pageUrl?: string;
  pageTitle?: string;
  pageDescription?: string;
  breadcrumbs?: { name: string; url: string }[];
  faqItems?: { question: string; answer: string }[];
  articleData?: {
    title: string;
    description: string;
    imageUrl: string;
    datePublished: string;
    dateModified: string;
    authorName: string;
  };
  contactInfo?: {
    email: string;
    wechat?: string;
  };
}

export default function SEOSchema({
  type,
  pageUrl,
  pageTitle,
  pageDescription,
  breadcrumbs,
  faqItems,
  articleData,
  contactInfo,
}: SEOSchemaProps) {
  const baseUrl = siteConfig.url;

  const orgSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteConfig.name,
    url: baseUrl,
    description: siteConfig.description,
    email: siteConfig.email,
    sameAs: [siteConfig.url],
  };

  if (contactInfo) {
    orgSchema.contactPoint = {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: contactInfo.email,
      availableLanguage: ["Chinese"],
    };
  }

  const schemas: Record<string, unknown>[] = [
    orgSchema,
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: siteConfig.name,
      url: baseUrl,
      description: siteConfig.description,
      potentialAction: {
        "@type": "SearchAction",
        target: `${baseUrl}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ];

  if (pageUrl) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: pageTitle || siteConfig.name,
      description: pageDescription || siteConfig.description,
      url: `${baseUrl}${pageUrl}`,
      isPartOf: {
        "@type": "WebSite",
        name: siteConfig.name,
        url: baseUrl,
      },
    });
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((item, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: item.name,
        item: `${baseUrl}${item.url}`,
      })),
    });
  }

  if (faqItems && faqItems.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }

  if (articleData) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: articleData.title,
      description: articleData.description,
      image: articleData.imageUrl,
      datePublished: articleData.datePublished,
      dateModified: articleData.dateModified,
      author: {
        "@type": "Person",
        name: articleData.authorName,
      },
      publisher: {
        "@type": "Organization",
        name: siteConfig.name,
      },
    });
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }}
    />
  );
}
